// src/hooks/sync/useFirestoreQuota.js
// ─────────────────────────────────────────────────────────────────────────────
//  Firestore quota usage monitoring hook.
//  Tracks local write counts and provides warnings when approaching
//  the Spark plan limits (50k reads/day, 20k writes/day).
//
//  NOTE: This tracks LOCAL app activity, not Firebase Console numbers.
//  Real usage should be monitored in Firebase Console > Usage.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useEffect, useCallback, useRef } from 'react';
import { useSyncStore } from './useSyncStore';
import { onQuotaEvent } from '@/lib/sync/syncManager';

const SPARK_WRITE_LIMIT = 20000;
const SPARK_READ_LIMIT = 50000;
const WARNING_THRESHOLD = 0.8; // Warn at 80% of limit

export function useFirestoreQuota() {
  const [writeCount, setWriteCount] = useState(0);
  const [readCount, setReadCount] = useState(0);
  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const lastResetRef = useRef(null);

  // Reset counters daily
  // Flag 15 fix: previously only ran once on mount ([] deps), so counters
  // never reset if the tab stayed open past midnight. Now re-checks on a
  // periodic interval too, so a day rollover while the tab is open is caught.
  useEffect(() => {
    const checkReset = () => {
      const today = new Date().toDateString();
      if (lastResetRef.current !== today) {
        lastResetRef.current = today;
        setWriteCount(0);
        setReadCount(0);
        setShowWarning(false);
      }
    };
    checkReset();
    const interval = setInterval(checkReset, 60_000);
    return () => clearInterval(interval);
  }, []);

  // Check thresholds
  useEffect(() => {
    const writePct = writeCount / SPARK_WRITE_LIMIT;
    const readPct = readCount / SPARK_READ_LIMIT;
    
    if (writePct >= WARNING_THRESHOLD || readPct >= WARNING_THRESHOLD) {
      setShowWarning(true);
      if (writePct >= WARNING_THRESHOLD) {
        setWarningMessage(`Spark write quota: ${writeCount}/${SPARK_WRITE_LIMIT} (${Math.round(writePct * 100)}%). Consider upgrading to Blaze plan.`);
      } else {
        setWarningMessage(`Spark read quota: ${readCount}/${SPARK_READ_LIMIT} (${Math.round(readPct * 100)}%). Consider upgrading to Blaze plan.`);
      }
    }
  }, [writeCount, readCount]);

  const trackWrite = useCallback((count = 1) => {
    setWriteCount(prev => prev + count);
  }, []);

  const trackRead = useCallback((count = 1) => {
    setReadCount(prev => prev + count);
  }, []);

  // Flag 16/25 fix: subscribe to syncManager's quota event bus so local
  // Firestore activity (writes via firestoreSet/writeBatch, reads via
  // onSnapshot fires) is tracked automatically — no consumer needs to call
  // trackWrite/trackRead manually.
  useEffect(() => {
    return onQuotaEvent(({ type, count }) => {
      if (type === 'write') trackWrite(count);
      else trackRead(count);
    });
  }, [trackWrite, trackRead]);

  const dismissWarning = useCallback(() => {
    setShowWarning(false);
  }, []);

  return {
    writeCount,
    readCount,
    writeLimit: SPARK_WRITE_LIMIT,
    readLimit: SPARK_READ_LIMIT,
    writePercentage: Math.round((writeCount / SPARK_WRITE_LIMIT) * 100),
    readPercentage: Math.round((readCount / SPARK_READ_LIMIT) * 100),
    showWarning,
    warningMessage,
    dismissWarning,
    trackWrite,
    trackRead,
    isSparkPlan: true, // Assume Spark by default - users upgrade to Blaze manually
  };
}